import useHasAccess from "@hooks/useHasAccess"
import useIsDesktop from "@hooks/useIsDesktop"
import useRouter from "@hooks/useRouter"
import useSlugExtractor from "@hooks/useSlugExtractor"

import extractSlugFromPathname from "@utils/extractSlugFromPathname"

import { AccessCheck } from "@particles"

import { Divider } from "@atoms/index"

import { NavTopBar, PageNavigation, ViewBar } from "@organisms"

import { AppFrame } from "@templates"

import { JOB_SERIES_ROUTES } from "@routes/jobs-series"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./JobSeriesDetails.module.scss"
import JobSeriesDetailsPanel from "./JobSeriesDetailsPanel/JobSeriesDetailsPanel"
import JobSeriesJobsTable from "./JobSeriesJobsTable/JobSeriesJobsTable"
import useJobSeriesDetails from "./useJobSeriesDetails"

function JobSeriesDetailsPanelContent() {
    const { pathname } = useRouter()
    const jobSeriesId = extractSlugFromPathname(pathname, JOB_SERIES_ROUTES.DETAILS, "id")
    const { jobSeries, refetch } = useJobSeriesDetails(jobSeriesId)

    return (
        <JobSeriesDetailsPanel
            jobSeries={jobSeries}
            onJobSeriesUpdate={() => {
                void refetch()
            }}
        />
    )
}

function JobSeriesJobsTableContent() {
    const { pathname } = useRouter()
    const jobSeriesId = extractSlugFromPathname(pathname, JOB_SERIES_ROUTES.DETAILS, "id")

    return (
        <AccessCheck permissions={{ jobs_list_permission: PERMISSION_LEVEL.FULL }}>
            <JobSeriesJobsTable jobSeriesId={jobSeriesId} />
        </AccessCheck>
    )
}

export default function JobSeriesDetails() {
    const [listRoute] = useSlugExtractor([JOB_SERIES_ROUTES.LIST])
    const isDesktop = useIsDesktop()

    const { hasPermission } = useHasAccess()

    const backDestination = hasPermission("jobs_list_permission", PERMISSION_LEVEL.FULL) ? listRoute : undefined

    if (isDesktop) {
        return (
            <AppFrame.Root>
                <NavTopBar.Root>
                    <NavTopBar.Header>
                        <NavTopBar.NavTopBarHeaderStart>
                            <NavTopBar.PageInnerTitle label="Job Series Details" backDestination={backDestination} />
                        </NavTopBar.NavTopBarHeaderStart>
                    </NavTopBar.Header>
                </NavTopBar.Root>
                <AppFrame.PageContent>
                    <div className={styles.layout}>
                        <div className={styles.main}>
                            <JobSeriesJobsTableContent />
                        </div>
                        <Divider contrast="low" orientation="vertical" />
                        <div className={styles.sidebar}>
                            <JobSeriesDetailsPanelContent />
                        </div>
                    </div>
                </AppFrame.PageContent>
            </AppFrame.Root>
        )
    } else {
        return (
            <PageNavigation.Root initialComponentId="details">
                <AppFrame.Root navigationType="none">
                    <NavTopBar.Root>
                        <NavTopBar.Header>
                            <NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.PageInnerTitle
                                    label="Job Series Details"
                                    backDestination={backDestination}
                                />
                            </NavTopBar.NavTopBarHeaderStart>
                        </NavTopBar.Header>
                        <ViewBar.Root>
                            <ViewBar.Start>
                                <PageNavigation.Tabs>
                                    <PageNavigation.Tab componentId="details" label="Details" />
                                    <PageNavigation.Tab componentId="jobs" label="Jobs" />
                                </PageNavigation.Tabs>
                            </ViewBar.Start>
                        </ViewBar.Root>
                    </NavTopBar.Root>
                    <AppFrame.PageContent>
                        <PageNavigation.Views>
                            <PageNavigation.View componentId="details">
                                <div className={styles.sidebar}>
                                    <JobSeriesDetailsPanelContent />
                                </div>
                            </PageNavigation.View>
                            <PageNavigation.View componentId="jobs">
                                <JobSeriesJobsTableContent />
                            </PageNavigation.View>
                        </PageNavigation.Views>
                    </AppFrame.PageContent>
                </AppFrame.Root>
            </PageNavigation.Root>
        )
    }
}
